import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { AiOutlineClose } from "react-icons/ai";
import { FaUser } from "react-icons/fa";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

Modal.setAppElement("#root");

// The user avatar button + its popup (name/email, Logout, Delete Account) and
// the delete-account confirmation modal. Extracted from Main so both the Home
// dashboard and the address list can show it without duplicating the
// sign-out / account-deletion logic. Fetches its own user info so callers don't
// have to thread it in. Renders inside a position:relative header; the popup is
// absolutely positioned relative to that header.
const ProfileMenu = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();
  const [userInfo, setUserInfo] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let docUnsub = () => {};
    const authUnsub = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      docUnsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
        if (snap.exists()) setUserInfo(snap.data());
      });
    });
    return () => {
      authUnsub();
      docUnsub();
    };
  }, [auth, db]);

  const handleLogout = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Also clears the native Google Sign-In session (see ADR-018 / Main's
        // original handler). Own try/catch: the native plugin can throw "No
        // user is signed in" even when the JS SDK state is valid; that must
        // not block the real sign-out below.
        try {
          await FirebaseAuthentication.signOut();
        } catch (nativeSignOutError) {
          console.warn(
            "Native FirebaseAuthentication.signOut() failed, continuing: ",
            nativeSignOutError,
          );
        }
      }
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error during logout: ", error);
    }
  };

  const handleDeleteAccount = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setDeleting(true);
    setDeleteError("");
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        "https://us-central1-one-address-53a06.cloudfunctions.net/deleteAccount",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(`deleteAccount returned ${response.status}`);
      }
      if (Capacitor.isNativePlatform()) {
        try {
          await FirebaseAuthentication.signOut();
        } catch (nativeSignOutError) {
          console.warn(
            "Native FirebaseAuthentication.signOut() failed, continuing: ",
            nativeSignOutError,
          );
        }
      }
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error deleting account: ", error);
      setDeleteError(
        "Couldn't delete your account right now. Please try again in a bit.",
      );
      setDeleting(false);
    }
  };

  if (userInfo === null) return null;

  return (
    <>
      <button
        onClick={() => setMenuVisible((v) => !v)}
        style={{
          fontSize: "20px",
          background: "none",
          border: "none",
          cursor: "pointer",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-primary, #1a1a1a)",
        }}
        aria-label="Profile"
      >
        <FaUser />
      </button>

      {menuVisible && (
        <div
          className="profile-modal container"
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            top: "40px",
            border: "1px solid var(--border-color, #ccc)",
            padding: "20px 16px 16px",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.15)",
            borderRadius: "14px",
            width: "95%",
            maxWidth: "340px",
            boxSizing: "border-box",
            alignItems: "left",
            zIndex: 10,
          }}
        >
          <AiOutlineClose
            className="close-icon"
            onClick={() => setMenuVisible(false)}
          />
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "#007bff",
              color: "white",
              fontSize: "22px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
            }}
          >
            {(userInfo.firstName || "?").charAt(0).toUpperCase()}
          </div>
          <strong className="profile-email" style={{ fontSize: "17px" }}>
            {userInfo.firstName} {userInfo.lastName}
          </strong>
          <p className="profile-email" style={{ fontSize: "14px" }}>
            {userInfo.email}
          </p>
          <hr
            style={{
              width: "100%",
              border: 0,
              borderTop: "1px solid var(--border-color, #eee)",
              margin: "4px 0 16px",
            }}
          />
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleLogout}
              style={{
                backgroundColor: "transparent",
                color: "var(--text-primary, #333)",
                padding: "10px 24px",
                border: "1px solid var(--border-color, #ccc)",
                cursor: "pointer",
                borderRadius: "20px",
              }}
            >
              Logout
            </button>
            <button
              onClick={() => setDeleteModalOpen(true)}
              style={{
                backgroundColor: "transparent",
                color: "#dc3545",
                padding: "10px 15px",
                border: "1px solid #dc3545",
                cursor: "pointer",
                borderRadius: "20px",
              }}
            >
              Delete Account
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={deleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        style={{
          content: {
            position: "relative",
            top: "auto",
            left: "auto",
            right: "auto",
            bottom: "auto",
            width: "80%",
            maxWidth: "400px",
            height: "auto",
            margin: "auto",
            padding: "20px",
            textAlign: "center",
            borderRadius: "10px",
            backgroundColor: "#fff",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "100px",
          },
        }}
      >
        <h3>Delete your account?</h3>
        <p>
          This permanently deletes your account, profile, and saved addresses.
          This action cannot be undone.
        </p>
        {deleteError && <p style={{ color: "red" }}>{deleteError}</p>}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={handleDeleteAccount}
            disabled={deleting}
            style={{
              backgroundColor: "red",
              color: "white",
              padding: "10px 15px",
              border: "none",
              cursor: deleting ? "default" : "pointer",
              borderRadius: "5px",
            }}
          >
            {deleting ? "Deleting..." : "Yes, Delete My Account"}
          </button>
          <button
            onClick={() => setDeleteModalOpen(false)}
            disabled={deleting}
            style={{
              backgroundColor: "gray",
              color: "white",
              padding: "10px 15px",
              border: "none",
              cursor: "pointer",
              borderRadius: "5px",
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ProfileMenu;
